import React from "react";
import { GOV_PROPOSAL_TEASER } from "./components/GovProposalTeaser";
import { GovMotionPublic } from "./components/GovMotionPublic";

/**
 * Written teasers for the governance module's gated routes
 * (vault/how-to/seamless-page-auth-pattern.md § Part 3). Each says what the
 * reader will be able to DO after signing in, in the module's register.
 *
 * WRITTEN, never CLIPPED: nothing here imports a page component or runs a
 * protected query — only this copy reaches a signed-out browser. The public
 * routes (`/org/:slug`, `/governance/invite/:slug`) are not gated and have none.
 */
export interface GovPreview {
  title: string;
  blurb: string;
  /** Rendered instead of title+blurb when present (e.g. a public-safe motion teaser). */
  node?: React.ReactNode;
}

/** Keyed by ROUTE TEMPLATE (as in moduleConfig.ts), not by concrete path. */
export const GOVERNANCE_PREVIEWS: Record<string, GovPreview> = {
  "/governance": {
    title: "Groups that decide things together",
    blurb:
      "Sign in to see the organisations you belong to, the motions open for a vote, and the work on offer. You land back here as soon as you are in.",
  },
  "/governance/marketplace": {
    title: "Standard jobs on offer",
    blurb:
      "Sign in to browse the jobs each group has agreed a price for, and start one if you are a member. Adopted rates only — nothing here is a draft.",
  },
  "/governance/:id": {
    title: "Your organisation's record",
    blurb:
      "Sign in to read the members, the motions, the work and the treasury, and the append-only log of who did what. Members can vote and claim work from here.",
  },
  "/governance/:id/join": {
    title: "Take part in this group",
    blurb:
      "Sign in to read the purpose and rules, see who the members are, and choose how much you want to be involved — follow, delegate your vote, or vote yourself.",
  },
  "/governance/:orgId/activity/:id": {
    title: "One thing that happened, on the record",
    blurb:
      "Sign in to read this entry in full — what happened, who did it, the documents attached to it, and the replies. A link like this one usually arrives because somebody tagged you by name.",
  },
  "/governance/:id/participation": {
    title: "How involved do you want to be",
    blurb:
      "Sign in to choose your level of participation and exactly which emails or texts you receive — every motion, a weekly report, or nothing until you say so.",
  },
  "/governance/:id/jobs": {
    title: "This group's standard jobs",
    blurb:
      "Sign in to read the jobs the group has priced, propose a new one, or start an instance of an adopted job. Every rate here was agreed by a vote or by the committee.",
  },
  "/governance/:id/treasury": {
    title: "Where the money is",
    blurb:
      "Sign in to see each account, where its balance came from and as at when, and every movement with the decision that authorised it.",
  },
  "/governance/:orgId/work/:id": {
    title: "A piece of work for this group",
    blurb:
      "Sign in to read the brief, claim the work if you are a member, and upload proof when it is done. Accepting proof and paying for it are two separate acts, both on the record.",
  },
  "/governance/:id/notifications": {
    title: "Who gets told what",
    blurb:
      "Sign in to set the rules for who is emailed or texted when a motion opens, a vote is closing, work is posted or someone joins. Nothing sends until a rule exists.",
  },
  "/governance/:id/invites": {
    title: "Invite someone to this group",
    blurb:
      "Sign in to send a private invitation link, see who has opened theirs, and follow the graph of who vouched for whom. An invitation gets someone to the door; the committee still decides.",
  },
  "/governance/:id/settings": {
    title: "How this group runs",
    blurb:
      "Sign in to change the quorum, the pass threshold, how long a vote stays open, how many sponsors a newcomer needs, and what the public can see. Changes apply to motions opened from now on.",
  },
  "/governance/:orgId/proposals/:id": {
    ...GOV_PROPOSAL_TEASER,
    // Shows the WHOLE motion when the group publishes at that level, and falls back to the short
    // teaser when it does not (2026-09-16). Neither imports a page component — previews.test pins it.
    node: React.createElement(GovMotionPublic),
  },
};

const OBJECT_ID = /^[a-f0-9]{24}$/i;

/** Does a concrete path match a route template segment-for-segment? */
const matches = (template: string, pathname: string): boolean => {
  const t = template.split("/").filter(Boolean);
  const p = pathname.split("/").filter(Boolean);
  if (t.length !== p.length) return false;
  return t.every((seg, i) => (seg.startsWith(":") ? OBJECT_ID.test(p[i]) || p[i].length > 0 : seg === p[i]));
};

/**
 * Longest-template-wins so `/governance/<id>/jobs` never falls to `/governance/:id`.
 * Accepts either a route TEMPLATE (what App.tsx hands us) or a concrete URL.
 */
export const governancePreviewForPath = (pathname: string): GovPreview | undefined => {
  const clean = pathname.replace(/\/$/, "") || "/";
  // Public education is not an organisation ID and must never inherit its sign-in teaser.
  if (["/governance/learn", "/governance/lab", "/governance/guide"].includes(clean)) return undefined;
  if (GOVERNANCE_PREVIEWS[clean]) return GOVERNANCE_PREVIEWS[clean];
  const hit = Object.keys(GOVERNANCE_PREVIEWS)
    .filter((tpl) => matches(tpl, clean))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? GOVERNANCE_PREVIEWS[hit] : undefined;
};
